/**
 * Recording a supplier's price that never came through an RFQ.
 *
 * ---- Why this exists ----
 *
 * Until now the ONLY way to file a supplier quotation was to parse an inbound EMAIL against an
 * RFQ (`parse_supplier_reply`, or reading their returned workbook). That covers one way a price
 * arrives. In practice a supplier quotes over the phone, on WhatsApp, or across a counter at
 * least as often — and those prices had nowhere to go, so the comparison never saw them and the
 * enquiry looked unanswered.
 *
 * ---- No RFQ, and that is deliberate ----
 *
 * `create_supplier_quotation` accepts an EMPTY `rfq`. Verified live 2026-08-31: a payload with
 * `rfq: ""` returned `SQ-2026-00272` with the right supplier and total. So a price can be
 * recorded against the enquiry without one ever having been sent — which is the whole point, and
 * something the field's presence in the API collection does not tell you.
 *
 * ---- What it refuses ----
 *
 * Nothing here rounds, converts or restates. The rate filed is the number the purchaser typed, in
 * the currency they named, against the line's own unit — `compare.ts` converts from raw facts and
 * doing it twice is how a price drifts from what the supplier actually said.
 *
 * A BLANK line is not filed — that is "they did not price this", and it must not become an offer.
 *
 * A typed ZERO is filed (amended 2026-09-07). The original rule refused it, on the grounds that a
 * decline would become an offer of free goods — but that conflated two things the purchaser can
 * state separately. Blank is the decline; 0 is a supplier saying zero, which happens (free issue,
 * no charge, included with another line), and refusing it meant the purchaser could not record
 * what they were told. The distinction is carried by the ABSENCE of an entry, not by its value, so
 * nothing is blurred: `rates` has no key for a blank line and a key holding 0 for a quoted zero.
 *
 * A NEGATIVE rate is still refused — it is not a price under any reading.
 */

import { printedDescription } from './internalNotes'
import type { CreateSupplierQuotationItemPayload, CreateSupplierQuotationPayload, EnquiryItem } from './types'

export interface ManualQuotationInput {
  enquiryId: string
  /** `Supplier.code` — the Link identity, never a display name. */
  supplier: string
  company: string
  currency: string
  /** `"YYYY-MM-DD"` — the day the supplier gave the price, which may not be today. */
  transactionDate: string
  lines: EnquiryItem[]
  /** `line_id` → the rate the purchaser keyed in. A line absent here was not priced. */
  rates: ReadonlyMap<string, number>
  /**
   * `line_id` → what the supplier said beyond the number: "ex-stock", "6 week lead", "min 10".
   *
   * `Supplier Quotation Item.remark` holds it and the comparison already renders it (`OfferCell`),
   * so a manual entry without it filed a price the purchaser could not annotate — the phone call
   * is exactly where those conditions get stated. Verified live 2026-09-06: the wire accepts
   * `remark` per item and reads it back.
   */
  remarks?: ReadonlyMap<string, string>
}

export type ManualQuotationResult =
  | { ok: true; payload: CreateSupplierQuotationPayload }
  | { ok: false; reason: string }

/*
  No `amount` is sent. It is not part of `CreateSupplierQuotationItemPayload`, and the server
  derives the line and document totals from `qty x rate` itself — the workbook path has always
  relied on that. Sending our own would be a second source of truth for a number the server
  already computes.
*/
export function buildManualQuotation(input: ManualQuotationInput): ManualQuotationResult {
  if (input.supplier.trim() === '') return { ok: false, reason: 'Choose the supplier who gave this price.' }
  // Never defaulted: a rate without a currency cannot be compared with anything.
  if (input.currency.trim() === '') return { ok: false, reason: 'Choose the currency these rates are in.' }

  const items: CreateSupplierQuotationItemPayload[] = []
  for (const line of input.lines) {
    const rate = input.rates.get(line.line_id)
    /*
      An ABSENT entry is "they did not price this"; a rate of 0 is "they quoted zero" and IS filed
      (Akhil, 2026-09-07 — see the header). Negative is neither, and is refused.
    */
    if (rate === undefined || !Number.isFinite(rate) || rate < 0) continue
    /*
      `Supplier Quotation Item.item_code` is a Frappe Link, so an unmatched line would fail the
      WHOLE document rather than just its row. Skipped, not sent blank.
    */
    if (!line.item_code) continue

    items.push({
      item_code: line.item_code,
      qty: line.qty,
      uom: line.uom,
      rate,
      /*
        The through-key, and it is load-bearing. `comparison.get_comparison` joins offers to
        enquiry rows on `enquiry_row_no`; a line without it files SUCCESSFULLY and is then
        invisible in the comparison — measured on SQ-2026-00010, which showed one supplier on a
        two-supplier enquiry. `cust_sno` IS that key (see `toEnquiryItem`), never `line_id`.
      */
      enquiry_row_no: line.cust_sno,
      // What the line prints — so the quotation reads as the purchaser wrote it, not as the master
      // names it (Akhil, 2026-10-16). Omitted when there is nothing to say.
      ...(printedDescription(line) ? { description: printedDescription(line) } : {}),
      /*
        Omitted entirely when empty rather than sent blank — an empty remark is not a remark, and
        writing '' over a field the parser may later fill costs information for nothing.
      */
      ...((input.remarks?.get(line.line_id) ?? '').trim() !== ''
        ? { remark: (input.remarks?.get(line.line_id) ?? '').trim() }
        : {}),
    })
  }

  if (items.length === 0) return { ok: false, reason: 'Enter a price for at least one line.' }

  return {
    ok: true,
    payload: {
      // Empty, not omitted — this quotation answers no RFQ. See the header.
      rfq: '',
      enquiry: input.enquiryId,
      supplier: input.supplier,
      transaction_date: input.transactionDate,
      currency: input.currency,
      company: input.company,
      items,
    },
  }
}
